import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import queryString from 'query-string';
import clone from 'ramda/src/clone';
import find from 'ramda/src/find';
import propEq from 'ramda/src/propEq';

import ErrorBoundary from '@components/@core/ErrorBoundary';
import Filter from '@components/@core/Filter';
import Search from '@components/LibarySearch';
import Status from '@components/LibraryStatus';
import * as QuotaCard from '@components/LibraryToDoQuota';
import AppActions from '~/actions/app';
import DevelopActions from '~/actions/develop';

const Quotas = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const templates = useSelector((state) => state.develop.templates);
    const userRole = useSelector((state) => state.app.app_role_id);
    const [filter, setFilter] = useState(queryString.parse(location?.search || '')?.filter || '');
    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const quotas = useMemo(() => templates['quotas'], [templates]);

    const handleSearch = (e) => {
        setFilter(e.target.value);
        const url = new URL(window.location);
        url.searchParams.set('filter', e.target.value);
        navigate(`${window.location.pathname}?${url.searchParams.toString()}`, { replace: true });
    };

    const handleToggleModal = (e) => dispatch(AppActions.modalRequest(e));

    const handleDeleteTemplate = (template) => {
        dispatch(
            DevelopActions.libraryeventRequest('quota_template', 'delete', template.id, null, null, {
                type: 'GETLIBRARYTEMPLATESSEARCH_REQUEST',
                target: 'quotas',
                payload: { search: filter, page, perPage },
            })
        );
    };

    const handleScorecardQuota = (e, card) => {
        const quotas = clone(e.data.quotas);
        const duplicated = find(propEq('quota_template_id', card.id), quotas);
        if (duplicated) {
            toast.error('The selected Quota is already in the Scorecard', {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
        } else {
            navigate(`/library/to-do/scorecards/${e.id}/edit`, {
                state: { card },
            });
        }
    };

    const handleProgramQuota = (e, card) => {
        const program = e.templates[0];
        const quotas = clone(program.data.levels[e.level].quotas);
        const duplicated = find(propEq('quota_template_id', card.id), quotas);
        if (duplicated) {
            toast.error('The selected Quota is already in the Scorecard', {
                position: toast.POSITION.TOP_RIGHT,
                pauseOnFocusLoss: false,
                hideProgressBar: true,
            });
        } else {
            dispatch(
                DevelopActions.libraryprogramdetailRequest(program, e.type, {
                    pathname: `/library/programs/${e.type}/${program.id}/edit`,
                    state: { card, level: e.level },
                })
            );
        }
    };

    const handlePublishQuota = (quota) => {
        const after = {
            type: 'GETLIBRARYTEMPLATESSEARCH_REQUEST',
            target: 'quotas',
            payload: {
                search: filter,
                page,
                perPage,
            },
        };
        const payload = {
            type: 'quotas',
            data: { quota_template: { ...quota, published: 1 } },
        };
        dispatch(DevelopActions.librarysaveRequest(payload, null, null, after));
    };

    const handleSelectMenu = useCallback(
        (event, card) => {
            switch (event) {
                case 'detail view': {
                    navigate(`/library/quotas/${card.id}/view`, {
                        state: { filter, page },
                    });
                    break;
                }
                case 'edit': {
                    navigate(`/library/quotas/${card.id}/edit`, {
                        state: { editable: true },
                    });
                    break;
                }
                case 'delete':
                    handleToggleModal({
                        type: 'Confirm',
                        data: { before: { title: 'Delete', body: 'Are you sure you want to delete this item?' } },
                        callBack: { onYes: () => handleDeleteTemplate(card) },
                    });
                    break;
                case 'attach to scorecard':
                    handleToggleModal({
                        type: 'Attach Library',
                        data: { before: { show: 'scorecards' } },
                        callBack: { onAttach: (e) => handleScorecardQuota(e.templates[0], card) },
                    });
                    break;
                case 'attach to program':
                    handleToggleModal({
                        type: 'Attach Library',
                        data: { before: { show: ['careers', 'certifications'], isProgram: true } },
                        callBack: { onAttach: (e) => handleProgramQuota(e, card) },
                    });
                    break;
                case 'publish':
                    handlePublishQuota(card);
                    break;
                default:
                    break;
            }
        },
        [filter, page, perPage]
    );

    const handleToggle = useCallback(
        (id) => {
            navigate(`/library/quotas/${id}/view`, {
                state: { filter, page },
            });
        },
        [filter, page]
    );

    const handleOpenQuota = () => {
        const payload = {
            type: 'Add New Quota',
            data: {
                before: { quota: null },
            },
        };
        handleToggleModal(payload);
    };

    useEffect(() => {
        dispatch(
            DevelopActions.getlibrarytemplatessearchRequest('quotas', {
                search: filter,
                page,
                perPage,
            })
        );
    }, [filter, page, perPage]);

    return (
        <ErrorBoundary>
            <Filter add="Goal" onAdd={handleOpenQuota} />
            <div className="card px-0 px-sm-4">
                <div className="d-flex jc-between px-4 px-sm-0 mb-4 pb-1">
                    <span className="dsl-b22 bold">Goals</span>
                    <Search initial={filter} onChange={handleSearch} />
                </div>
                <Status type="Goals" showToggle counts={quotas?.total} />
                <QuotaCard.List
                    role={userRole}
                    data={quotas}
                    onChange={(e) => setPage(e)}
                    onPer={(e) => setPerPage(e)}
                    onModal={handleToggleModal}
                    onSelect={handleSelectMenu}
                    onToggle={handleToggle}
                    onAddQuota={handleOpenQuota}
                />
            </div>
        </ErrorBoundary>
    );
};

export default Quotas;
